import type { Cell, RowData } from "@tanstack/react-table";
import type { ReactNode } from "react";
import { cx } from "@/utils/cx";
import type { AppTableFeatures } from "./app-table-features";
import { getTableColumnMeta, isActionsColumnId } from "./table-column-meta";

/**
 * Clip table cell content to a single line with ellipsis.
 * Actions columns and columns with `meta.disableCellClamp` are left unclamped.
 *
 * Plain text (`p`, inline description `span`s) clips on the wrapper —
 * badges / labels / icon+text flex rows keep their own layout.
 */
export function wrapTableCellClamp<TData extends RowData>(
	columnId: string,
	meta: unknown,
	cell: Cell<AppTableFeatures, TData, unknown>,
	content: ReactNode,
): ReactNode {
	const columnMeta = getTableColumnMeta(meta);
	if (
		isActionsColumnId(columnId) ||
		columnMeta?.disableCellClamp ||
		columnMeta?.disableDescriptionClamp
	) {
		return content;
	}

	const rawValue = cell.getValue();
	const title =
		typeof rawValue === "string" && rawValue.trim().length > 0
			? rawValue
			: undefined;

	return (
		<div
			className={cx(
				// Horizontal clamp only — overflow-y:hidden clips Manrope descenders (g/y/p)
				"min-w-0 w-full max-w-full overflow-x-clip overflow-y-visible text-ellipsis whitespace-nowrap",
				// Name cells that render <p> (possibly nested under icon+text)
				"[&_p]:min-w-0 [&_p]:max-w-full [&_p]:overflow-x-clip [&_p]:overflow-y-visible [&_p]:text-ellipsis [&_p]:whitespace-nowrap",
			)}
			title={title}
		>
			{content}
		</div>
	);
}
